// lookbookApi.js
//
// PURPOSE:
// Lookbook service API module for managing lookbook collection photoshoots.
// Provides functions for fetching lookbooks and creating new lookbooks (admin-only).
// Lookbooks display curated images from fashion collections.
//
// SECURITY NOTES:
// - Creating lookbooks requires Firebase authentication token
// - Server validates admin authorization on create operations
// - All responses from server are validated before use
//
// OPTIMIZATION NOTES:
// - Lookbooks are cached by client (useLookbook hook)
// - Images are loaded lazily from VPS on demand
// - Fetches are batched to reduce API calls
//

import apiRequest from './api';

// Fetches all lookbooks from the server
// SECURITY: No authentication required for read access
// OPTIMIZATION: Returns all lookbooks at once; client can paginate if needed
// Returns: array of lookbook objects with images
export async function getAllLookbooks() {
  return apiRequest('/Lookbook', { method: 'GET' });
}

// Fetches a single lookbook by ID
// SECURITY: No authentication required for read access
// Parameters: lookbook ID (string)
// Returns: lookbook object with full details and all images
export async function getLookbook(id) {
  return apiRequest(`/Lookbook/${id}`, { method: 'GET' });
}

// Creates a new lookbook (admin-only operation)
// SECURITY: Requires Firebase authentication token
// Server will verify admin authorization before creating
// Parameters:
//   lookbook: object with { name, description, images }
//   idToken: Firebase ID token for authorization
// Returns: created lookbook object with generated ID
export async function createLookbook(lookbook, idToken) {
  return apiRequest('/Lookbook', {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(lookbook),
  });
}

// Updates an existing lookbook (admin-only operation)
// SECURITY: Requires Firebase authentication token
// Parameters:
//   id: lookbook document ID
//   lookbook: object with updated data
//   idToken: Firebase ID token for authorization
// Returns: updated lookbook object
export async function updateLookbook(id, lookbook, idToken) {
  return apiRequest(`/Lookbook/${id}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(lookbook),
  });
}

// Deletes a lookbook (admin-only operation)
// SECURITY: Requires Firebase authentication token
// Parameters:
//   id: lookbook document ID
//   idToken: Firebase ID token for authorization
// Returns: no content on success
export async function deleteLookbook(id, idToken) {
  return apiRequest(`/Lookbook/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${idToken}` },
  });
}
